'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

type BookingStatus = 'confirmed' | 'active' | 'pending' | 'completed' | 'cancelled';

const bookingsData = [
  {
    id: 'bkg-001',
    ref: 'BKG-9041',
    customer: 'Rachel Kim',
    customerEmail: 'rachel.kim@email.com',
    service: 'Skyline Penthouse Suite',
    category: 'Apartment',
    startDate: 'Apr 22, 2026',
    endDate: 'May 22, 2026',
    amount: 4200,
    status: 'active' as BookingStatus,
    paymentStatus: 'settled',
    assignedTo: 'Jonathan Reeves',
  },
  {
    id: 'bkg-002',
    ref: 'BKG-9040',
    customer: 'David Okonkwo',
    customerEmail: 'd.okonkwo@corp.com',
    service: 'Priya Sharma — Maid',
    category: 'Maid',
    startDate: 'Apr 22, 2026',
    endDate: 'Apr 22, 2026',
    amount: 112,
    status: 'confirmed' as BookingStatus,
    paymentStatus: 'pending',
    assignedTo: 'Diana Osei',
  },
  {
    id: 'bkg-003',
    ref: 'BKG-9039',
    customer: 'Samantha Torres',
    customerEmail: 'sam.torres@gmail.com',
    service: 'Premium Butler Service',
    category: 'Hospitality',
    startDate: 'Apr 23, 2026',
    endDate: 'Apr 23, 2026',
    amount: 340,
    status: 'confirmed' as BookingStatus,
    paymentStatus: 'stored',
    assignedTo: 'Jonathan Reeves',
  },
  {
    id: 'bkg-004',
    ref: 'BKG-9038',
    customer: 'Marcus Webb',
    customerEmail: 'mwebb@webdev.io',
    service: 'Garden View Studio',
    category: 'Apartment',
    startDate: 'Apr 18, 2026',
    endDate: 'May 18, 2026',
    amount: 1450,
    status: 'active' as BookingStatus,
    paymentStatus: 'settled',
    assignedTo: 'Diana Osei',
  },
  {
    id: 'bkg-005',
    ref: 'BKG-9037',
    customer: 'Yuki Tanaka',
    customerEmail: 'yuki.t@outlook.com',
    service: 'Spa & Wellness Session',
    category: 'Hospitality',
    startDate: 'Apr 20, 2026',
    endDate: 'Apr 20, 2026',
    amount: 220,
    status: 'pending' as BookingStatus,
    paymentStatus: 'pending',
    assignedTo: 'Marcus Chen',
  },
  {
    id: 'bkg-006',
    ref: 'BKG-9036',
    customer: 'Ibrahim Al-Sayed',
    customerEmail: 'ibrahim@alsayed.ae',
    service: 'Luxury Car Service',
    category: 'Hospitality',
    startDate: 'Apr 20, 2026',
    endDate: 'Apr 20, 2026',
    amount: 475,
    status: 'completed' as BookingStatus,
    paymentStatus: 'settled',
    assignedTo: 'Jonathan Reeves',
  },
  {
    id: 'bkg-007',
    ref: 'BKG-9035',
    customer: 'Preethi Nair',
    customerEmail: 'preethi.n@nair.in',
    service: 'Grace Okonkwo — Maid',
    category: 'Maid',
    startDate: 'Apr 18, 2026',
    endDate: 'Apr 18, 2026',
    amount: 90,
    status: 'completed' as BookingStatus,
    paymentStatus: 'overdue',
    assignedTo: 'Diana Osei',
  },
  {
    id: 'bkg-008',
    ref: 'BKG-9034',
    customer: 'Lena Hoffmann',
    customerEmail: 'l.hoffmann@dmail.de',
    service: 'Heritage Brownstone 2BR',
    category: 'Apartment',
    startDate: 'Apr 15, 2026',
    endDate: 'May 15, 2026',
    amount: 2800,
    status: 'active' as BookingStatus,
    paymentStatus: 'settled',
    assignedTo: 'Marcus Chen',
  },
  {
    id: 'bkg-009',
    ref: 'BKG-9033',
    customer: 'Carlos Mendez',
    customerEmail: 'c.mendez@hotmail.com',
    service: 'Maria Santos — Maid',
    category: 'Maid',
    startDate: 'Apr 24, 2026',
    endDate: 'Apr 24, 2026',
    amount: 100,
    status: 'confirmed' as BookingStatus,
    paymentStatus: 'pending',
    assignedTo: 'Diana Osei',
  },
  {
    id: 'bkg-010',
    ref: 'BKG-9032',
    customer: 'Ananya Krishnan',
    customerEmail: 'ananya.k@techco.in',
    service: 'Event Coordination',
    category: 'Hospitality',
    startDate: 'Apr 30, 2026',
    endDate: 'Apr 30, 2026',
    amount: 1200,
    status: 'cancelled' as BookingStatus,
    paymentStatus: 'settled',
    assignedTo: 'Jonathan Reeves',
  },
];

const paymentStyle: Record<string, string> = {
  settled: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-amber-50 text-amber-700',
  stored: 'bg-violet-50 text-violet-700',
  overdue: 'bg-red-50 text-red-700',
};

const categoryColors: Record<string, string> = {
  Apartment: 'bg-blue-50 text-blue-700',
  Maid: 'bg-teal-50 text-teal-700',
  Hospitality: 'bg-indigo-50 text-indigo-700',
};

export default function BookingsTable() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortCol, setSortCol] = useState('ref');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const perPage = 8;

  const filtered = bookingsData
    .filter((b) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        b.ref.toLowerCase().includes(q) ||
        b.customer.toLowerCase().includes(q) ||
        b.service.toLowerCase().includes(q);
      const matchStatus = filterStatus === 'all' || b.status === filterStatus;
      const matchCategory = filterCategory === 'all' || b.category === filterCategory;
      return matchSearch && matchStatus && matchCategory;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortCol === 'amount') return (a.amount - b.amount) * dir;
      if (sortCol === 'customer') return a.customer.localeCompare(b.customer) * dir;
      return a.ref.localeCompare(b.ref) * dir;
    });

  const toggleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const toggleRow = (id: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === filtered.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filtered.map((b) => b.id)));
    }
  };

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const handleBulkAction = (action: string) => {
    // TODO: POST /api/bookings/bulk with selectedRows and action
    toast.success(`${action} applied to ${selectedRows.size} booking(s).`);
    setSelectedRows(new Set());
  };

  const SortIcon = ({ col }: { col: string }) => (
    <span className="ml-1 inline-flex flex-col gap-px">
      <svg width="8" height="5" viewBox="0 0 8 5" className={sortCol === col && sortDir === 'asc' ? 'text-primary' : 'text-muted-foreground/40'}>
        <path d="M4 0L8 5H0L4 0Z" fill="currentColor"/>
      </svg>
      <svg width="8" height="5" viewBox="0 0 8 5" className={sortCol === col && sortDir === 'desc' ? 'text-primary' : 'text-muted-foreground/40'}>
        <path d="M4 5L0 0H8L4 5Z" fill="currentColor"/>
      </svg>
    </span>
  );

  return (
    <div className="bg-white rounded-xl border border-border shadow-card overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <div>
          <h2 className="font-display font-semibold text-base text-foreground">All Bookings</h2>
          <p className="text-xs text-muted-foreground">{filtered.length} total bookings</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-muted">
            <Icon name="MagnifyingGlassIcon" size={15} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Search bookings..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="bg-transparent text-sm outline-none w-36 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-border rounded-lg text-sm text-foreground bg-white outline-none cursor-pointer"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          {/* Category filter */}
          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-border rounded-lg text-sm text-foreground bg-white outline-none cursor-pointer"
          >
            <option value="all">All Categories</option>
            <option value="Apartment">Apartments</option>
            <option value="Maid">Maids</option>
            <option value="Hospitality">Hospitality</option>
          </select>
          <button
            onClick={() => toast.success('Bookings exported to CSV.')}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="ArrowDownTrayIcon" size={15} />
            Export
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedRows.size > 0 && (
        <div className="flex items-center gap-3 px-6 py-3 bg-primary/5 border-b border-primary/20">
          <span className="text-sm font-medium text-primary">{selectedRows.size} selected</span>
          <button
            onClick={() => handleBulkAction('Confirm')}
            className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-lg hover:bg-primary-light transition-colors"
          >
            Confirm All
          </button>
          <button
            onClick={() => handleBulkAction('Cancel')}
            className="px-3 py-1.5 text-xs font-semibold bg-destructive text-white rounded-lg hover:opacity-90 transition-colors"
          >
            Cancel All
          </button>
          <button
            onClick={() => setSelectedRows(new Set())}
            className="px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-muted transition-colors text-muted-foreground"
          >
            Deselect
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[1000px]">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedRows.size === filtered.length && filtered.length > 0}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-primary rounded"
                />
              </th>
              {[
                { key: 'ref', label: 'Reference' },
                { key: 'customer', label: 'Customer' },
                { key: 'service', label: 'Service' },
                { key: 'category', label: 'Category' },
                { key: 'dates', label: 'Dates' },
                { key: 'amount', label: 'Amount' },
                { key: 'status', label: 'Status' },
                { key: 'payment', label: 'Payment' },
                { key: 'assigned', label: 'Assigned To' },
              ].map((col) => (
                <th
                  key={`th-${col.key}`}
                  onClick={() => ['ref', 'customer', 'amount'].includes(col.key) ? toggleSort(col.key) : undefined}
                  className={`px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${
                    ['ref', 'customer', 'amount'].includes(col.key) ? 'cursor-pointer hover:text-foreground select-none' : ''
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {['ref', 'customer', 'amount'].includes(col.key) && <SortIcon col={col.key} />}
                  </span>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="CalendarDaysIcon" size={24} className="text-muted-foreground" />
                    </div>
                    <p className="font-display font-semibold text-foreground">No bookings found</p>
                    <p className="text-sm text-muted-foreground">Try adjusting your search or filter criteria</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((booking, idx) => (
                <tr
                  key={booking.id}
                  className={`border-b border-border/50 hover:bg-muted/30 transition-colors group ${
                    idx % 2 === 0 ? '' : 'bg-muted/10'
                  } ${selectedRows.has(booking.id) ? 'bg-primary/5' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(booking.id)}
                      onChange={() => toggleRow(booking.id)}
                      className="w-4 h-4 accent-primary rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono font-bold text-primary">{booking.ref}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-foreground whitespace-nowrap">{booking.customer}</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[140px]">{booking.customerEmail}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground max-w-[160px] truncate">{booking.service}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${categoryColors[booking.category]}`}>
                      {booking.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-foreground whitespace-nowrap">{booking.startDate}</p>
                    {booking.endDate !== booking.startDate && (
                      <p className="text-xs text-muted-foreground">→ {booking.endDate}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-bold text-foreground tabular-nums">${booking.amount.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={booking.status} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${paymentStyle[booking.paymentStatus]}`}>
                      {booking.paymentStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-muted-foreground whitespace-nowrap">{booking.assignedTo}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        title="View booking details"
                        onClick={() => toast.success(`Viewing ${booking.ref}`)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Icon name="EyeIcon" size={15} />
                      </button>
                      <button
                        title="Edit booking"
                        onClick={() => toast.success(`Editing ${booking.ref}`)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Icon name="PencilSquareIcon" size={15} />
                      </button>
                      <button
                        title="Cancel booking — this cannot be undone"
                        onClick={() => toast.error(`Booking ${booking.ref} cancelled.`)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Icon name="XMarkIcon" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-border">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)}</span> of{' '}
          <span className="font-semibold text-foreground">{filtered.length}</span> bookings
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-8 h-8 text-sm rounded-lg transition-colors ${
                page === i + 1
                  ? 'bg-primary text-white font-semibold' :'border border-border hover:bg-muted text-foreground'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}